import { type ReactNode, Suspense, useState } from "react";
import { useSearchParams } from "react-router";
import { Flex, SidePanel, Spinner } from "@wayflyer/flyui";
import { usePostHog } from "posthog-js/react";
import { filteredAndSortedDataSourcesNext, getAccountingConnectors } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";
import type { FundingApplicationSchemas } from "api-types";
import { useIntl } from "react-intl";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import { getProviderType } from "../../../components/constants";
import {
  AmazonConnect,
  BigcommerceConnectOtp,
  CodatConnect,
  GoogleAdsConnect,
  GoogleAnalyticsConnect,
  MagentoConnect,
  MetaAdsConnect,
  MockifyConnect,
  NetsuiteConnect,
  QuickbooksConnect,
  ShopifyAppStoreConnect,
  ShopifyConnect,
  StripeConnect,
  TikTokConnect,
  WooCommerceConnect,
  XeroConnect,
} from "../../../components";
import { AuthCallbackPath } from "../../../api-types/connector-callbacks";
import { usePlatformConnectors } from "../../../queries/connectors/queries";
import {
  useTriggerValidation,
  useValidatedDocumentQuery,
} from "../../../queries/document-uploads/validated-documents-queries";
import { useFundingApplication, useFundingApplicationMutation } from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";

import { AccountingUploadPanel } from "./accounting-upload-panel";

type FileUpload = FundingApplicationSchemas.File | FundingApplicationSchemas.ValidatedFile;

const isValidatedFile = (file: FileUpload): file is FundingApplicationSchemas.ValidatedFile =>
  "validationStatus" in file;

const readyFileIds = (files: FileUpload[]): string[] =>
  files
    .filter(isValidatedFile)
    .filter((f) => f.validationStatus === "ready")
    .map((f) => f.id);

const createPlatformComponent = (slug: string, onStarted: () => void): ReactNode => {
  const components: Record<string, ReactNode> = {
    shopify2: (
      <ShopifyConnect
        postConnectPath={AuthCallbackPath.FUNDING_APPLICATION_ADD_CONNECTION}
        onInitiateConnection={onStarted}
      />
    ),
    "shopify-appstore": (
      <ShopifyAppStoreConnect
        postConnectPath={AuthCallbackPath.FUNDING_APPLICATION_ADD_CONNECTION}
        fullWidthImage
        onInitiateConnection={onStarted}
      />
    ),
    amazonrutter: (
      <AmazonConnect
        postConnectPath={AuthCallbackPath.FUNDING_APPLICATION_ADD_CONNECTION}
        onInitiateConnection={onStarted}
      />
    ),
    stripe2: <StripeConnect onInitiateConnection={onStarted} />,
    woocommerce2: (
      <WooCommerceConnect
        postConnectPath={AuthCallbackPath.FUNDING_APPLICATION_ADD_CONNECTION}
        onInitiateConnection={onStarted}
      />
    ),
    bigcommercerutter: (
      <BigcommerceConnectOtp
        postConnectPath={AuthCallbackPath.FUNDING_APPLICATION_ADD_CONNECTION}
        onInitiateConnection={onStarted}
      />
    ),
    magento2: (
      <MagentoConnect
        postConnectPath={AuthCallbackPath.FUNDING_APPLICATION_ADD_CONNECTION}
        onInitiateConnection={onStarted}
      />
    ),
    mockify: (
      <MockifyConnect
        postConnectPath={AuthCallbackPath.FUNDING_APPLICATION_ADD_CONNECTION}
        onInitiateConnection={onStarted}
      />
    ),
    ga: <GoogleAnalyticsConnect onInitiateConnection={onStarted} />,
    google_ads2: <GoogleAdsConnect onInitiateConnection={onStarted} />,
    facebook2: <MetaAdsConnect onInitiateConnection={onStarted} />,
    tiktok: <TikTokConnect onInitiateConnection={onStarted} />,
    codat_accounting: <CodatConnect onInitiateConnection={onStarted} />,
    xero: <XeroConnect onInitiateConnection={onStarted} />,
    netsuite: <NetsuiteConnect onInitiateConnection={onStarted} />,
    quickbooks: <QuickbooksConnect onInitiateConnection={onStarted} />,
  };
  return components[slug] || null;
};

const PlatformNames: Record<string, string> = {
  shopify2: "Shopify",
  "shopify-appstore": "Shopify App Store",
  amazonrutter: "Amazon",
  stripe2: "Stripe",
  woocommerce2: "WooCommerce",
  bigcommercerutter: "BigCommerce",
  magento2: "Magento",
  mockify: "Mockify",
  ga: "Google Analytics",
  google_ads2: "Google Ads",
  facebook2: "Meta Ads",
  tiktok: "TikTok",
  codat_accounting: "Codat Accounting",
  accounting_upload: "Accounting Documents",
  xero: "Xero",
  netsuite: "NetSuite",
  quickbooks: "QuickBooks",
};

const getPlatformFromSearchParam = (searchParams: URLSearchParams): string | null => {
  if (searchParams.has("platform")) {
    return searchParams.get("platform");
  } else if (searchParams.has("complete-connection")) {
    return searchParams.get("complete-connection");
  }
  return searchParams.get("connectionSucceed");
};

export const ConnectedPlatform = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [uploadValidationAttempted, setUploadValidationAttempted] = useState(false);
  const [pendingCurrency, setPendingCurrency] = useState<string | undefined>();
  const platform = getPlatformFromSearchParam(searchParams);
  const posthog = usePostHog();

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const { data: validatedDocuments } = useValidatedDocumentQuery(
    customerDomain,
    fundingApplication.id,
    platform === "accounting_upload"
  );
  const { mutate: triggerPLValidation } = useTriggerValidation(
    customerDomain,
    fundingApplication.id,
    UploadDocumentType.PROFIT_AND_LOSS
  );
  const { mutate: triggerBSValidation } = useTriggerValidation(
    customerDomain,
    fundingApplication.id,
    UploadDocumentType.BALANCE_SHEET
  );
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const { data: connectors } = usePlatformConnectors(customerDomain, { skipConnections: true });
  const filteredDataSources = filteredAndSortedDataSourcesNext(connectors);

  const onlineSalesPlatforms = filteredDataSources.shoppingDataSources;
  const marketingPlatforms = filteredDataSources.marketingDataSources;
  const accountingPlatforms = getAccountingConnectors(filteredDataSources);

  const allPlatforms = [...onlineSalesPlatforms, ...marketingPlatforms, ...accountingPlatforms];
  const isPlatformAvailable = platform
    ? allPlatforms.some((p) => p.slug === platform) || platform === "accounting_upload"
    : false;
  const selectedPlatform = allPlatforms.find((p) => p.slug === platform);
  const platformTitle = selectedPlatform?.name || PlatformNames[platform || ""] || "";

  const panelTitle =
    platform === "accounting_upload" ? "Upload accounting documents" : platformTitle ? `Connect ${platformTitle}` : "";

  const isAccountingUploadPanel = platform === "accounting_upload";

  const validateAccountingUpload = (): boolean => {
    const plFiles = validatedDocuments?.find((d) => d.documentType === UploadDocumentType.PROFIT_AND_LOSS)?.data ?? [];
    const bsFiles = validatedDocuments?.find((d) => d.documentType === UploadDocumentType.BALANCE_SHEET)?.data ?? [];
    const effectiveCurrency = pendingCurrency ?? fundingApplication.accountingCurrency;
    const missingFileType = plFiles.length === 0 || bsFiles.length === 0;
    const missingCurrency = !effectiveCurrency && !fundingApplication.accountingOptedOut;

    return !missingFileType && !missingCurrency;
  };

  const handleStarted = () => {
    if (platform) {
      posthog?.capture("onboarding_connection_started", {
        provider: platform,
        provider_type: getProviderType(platform),
        method: "oauth",
      });
    }
  };

  const handleClose = () => {
    const didConnect = searchParams.has("connectionSucceed") || searchParams.has("complete-connection");
    const hadError = searchParams.has("oauth-error");
    if (platform && !isAccountingUploadPanel && !didConnect && !hadError) {
      posthog?.capture("onboarding_connection_abandoned", {
        provider: platform,
        provider_type: getProviderType(platform),
      });
    }
    setUploadValidationAttempted(false);
    setPendingCurrency(undefined);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("platform");
    newSearchParams.delete("codat_platform");
    newSearchParams.delete("complete-connection");
    newSearchParams.delete("connectionSucceed");
    newSearchParams.delete("oauth-error");
    setSearchParams(newSearchParams);
  };

  const handleDone = () => {
    if (isAccountingUploadPanel) {
      if (!validateAccountingUpload()) {
        setUploadValidationAttempted(true);
        return;
      }

      const plFiles =
        validatedDocuments?.find((d) => d.documentType === UploadDocumentType.PROFIT_AND_LOSS)?.data ?? [];
      const bsFiles = validatedDocuments?.find((d) => d.documentType === UploadDocumentType.BALANCE_SHEET)?.data ?? [];

      fundingApplicationMutation.mutate(
        {
          id: fundingApplication.id,
          accountingDocumentsRequired: true,
          ...(pendingCurrency && { accountingCurrency: pendingCurrency as FundingApplicationSchemas.Currency }),
        },
        {
          onSuccess: () => {
            posthog?.capture("onboarding_accounting_documents_uploaded", {
              pl_file_count: plFiles.length,
              bs_file_count: bsFiles.length,
            });
          },
        }
      );

      const plIds = readyFileIds(plFiles);
      const bsIds = readyFileIds(bsFiles);
      if (plIds.length > 0) {
        triggerPLValidation(plIds);
      }
      if (bsIds.length > 0) {
        triggerBSValidation(bsIds);
      }
    }

    handleClose();
  };

  return (
    <SidePanel
      open={isPlatformAvailable}
      isModal={false}
      onClose={handleClose}
      title={panelTitle}
      closeOnClickElseWhere={false}
      variant="md"
      primaryAction={{
        content: isAccountingUploadPanel
          ? intl.formatMessage(COMMON_MESSAGES.continueLabel)
          : intl.formatMessage(COMMON_MESSAGES.doneLabel),
        onAction: handleDone,
      }}
    >
      {platform ? (
        <Suspense
          fallback={
            <Flex justify="center" align="center" height="100%">
              <Spinner size={32} />
            </Flex>
          }
        >
          {isAccountingUploadPanel ? (
            <AccountingUploadPanel
              validationAttempted={uploadValidationAttempted}
              selectedCurrency={pendingCurrency ?? fundingApplication.accountingCurrency ?? undefined}
              onCurrencyChange={setPendingCurrency}
            />
          ) : (
            createPlatformComponent(platform, handleStarted)
          )}
        </Suspense>
      ) : null}
    </SidePanel>
  );
};
